import React from 'react';
import './LegalPages.css';

const Terms: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <header className="page-header">
          <h1>📜 Terms & Conditions</h1>
          <p className="date">Last updated: June 13, 2026</p>
        </header>

        <div className="legal-content">
          <p className="intro">
            By downloading, installing or using the <strong>Flow XP</strong> mobile application, you agree to be bound by these
            Terms and Conditions of Use. If you do not accept these terms, please do not use the application.
          </p>

          <div className="contact-info-box">
            <p><strong>Publisher:</strong> Flowledge</p>
            <p><strong>Contact:</strong> <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a></p>
          </div>

          <section className="legal-section">
            <h2>1. Application Overview</h2>
            <p>Flow XP is a gamified sports and social sports network mobile application that allows users to:</p>
            <ul>
              <li>Choose a training specialization (Powerflow, Overflow, Masterflow)</li>
              <li>Progress through training levels and challenges</li>
              <li>Submit training videos for validation</li>
              <li>Participate in peer-to-peer evaluations</li>
              <li>Interact with a sports community</li>
              <li>Participate in the "One Flow" daily challenge</li>
              <li>Earn points (Flow) and unlock rewards</li>
              <li>Use the Forge to create virtual items</li>
              <li>Make purchases in the integrated Shop</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. Acceptance of Conditions</h2>
            <h3>Minimum Age:</h3>
            <p>
              You must be of legal age in your country of residence to use this application. If you are a minor, you must
              obtain explicit consent from your parents or legal guardians.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Account Creation and Security</h2>
            <p>To use the application, you must create an account by providing:</p>
            <ul>
              <li>A valid email address</li>
              <li>A secure password</li>
              <li>A username (nickname)</li>
              <li>A profile picture (optional)</li>
            </ul>
            <p>
              You are solely responsible for the confidentiality of your login credentials and all activities performed under
              your account.
            </p>
            <p className="highlight-text">
              ⚠️ You can delete your account at any time from the app settings. Deletion is permanent and results in the loss
              of all your data.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Personal Data and Privacy</h2>
            <p>
              The application collects and processes personal data as described in our{' '}
              <a href="/privacy">Privacy Policy</a>. By using Flow XP, you consent to this data processing in accordance with
              GDPR regulations.
            </p>
            <p>
              For more information about your data rights, please read our <a href="/privacy">Privacy Policy</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. User Content</h2>
            <h3>Ownership:</h3>
            <p>You retain full intellectual property rights to the content you create and share on the app.</p>

            <h3>Content Rules - You agree NOT to post content that is:</h3>
            <ul>
              <li>Illegal, offensive, defamatory, obscene or pornographic</li>
              <li>Violent, hateful, racist or discriminatory</li>
              <li>Violating the intellectual property rights of others</li>
              <li>Containing personal information of third parties without consent</li>
              <li>Promoting illegal or dangerous activities</li>
              <li>Constituting spam or deceptive commercial practices</li>
            </ul>

            <p className="highlight-text">
              ⚠️ We reserve the right to moderate, delete or refuse any content violating these rules and to suspend or delete
              your account in case of repeated violations.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Virtual Currency and Subscriptions</h2>
            <h3>Virtual Currency (Flow):</h3>
            <p>
              The app uses virtual currency called "Flow" (points) which allows you to unlock virtual items, use the Forge,
              and access exclusive content. Flow is earned for free through gameplay.
            </p>

            <h3>Premium Subscriptions:</h3>
            <p>Flow XP offers paid premium subscriptions with automatic renewal:</p>
            <ul>
              <li><strong>Monthly Premium:</strong> Access to premium features for 1 month</li>
              <li><strong>Annual Premium:</strong> Access to premium features for 1 year (savings compared to monthly)</li>
              <li><strong>Founder:</strong> Lifetime premium access (one-time payment)</li>
            </ul>

            <h3>Premium Benefits:</h3>
            <ul>
              <li>Exclusive customizations (avatars, badges, themes)</li>
              <li>Premium badge visible on your profile and in rankings</li>
              <li>Priority access to new features</li>
              <li>Exclusive content and challenges</li>
              <li>Ad-free experience</li>
            </ul>

            <h3>Subscription Management:</h3>
            <p>
              Subscriptions are managed through your device's platform (App Store or Google Play). Payment will be charged to
              your platform account (Apple ID or Google Play) upon purchase confirmation. The subscription automatically renews
              unless you cancel at least 24 hours before the end of the current period.
            </p>
            <ul>
              <li><strong>On iOS:</strong> Manage and cancel subscriptions in Settings → [your name] → Subscriptions</li>
              <li><strong>On Android:</strong> Manage and cancel subscriptions in your Google Play account settings</li>
            </ul>

            <p className="highlight-text">
              ⚠️ Subscriptions and purchased virtual items are non-refundable, except for legal obligations (14-day withdrawal
              right in Europe if subscription has not started) or proven technical error on our part.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Application Permissions</h2>
            <p>The application requests the following permissions:</p>
            <ul>
              <li><strong>Camera and microphone:</strong> To record and submit your training videos</li>
              <li><strong>Storage:</strong> To save and access your photos and videos</li>
              <li><strong>Notifications:</strong> To receive app alerts (can be disabled in device settings)</li>
              <li><strong>Advertising identifier (iOS only):</strong> Flow XP requests permission via App Tracking Transparency (ATT) to personalize ads and measure performance. You can decline or change this in Settings → Privacy → Tracking.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Advertising</h2>
            <p>
              The app displays ads via Google AdMob to fund the free service. Singular is used for install attribution and
              performance measurement. You can disable personalized ads in your device settings:
            </p>
            <ul>
              <li><strong>On iOS:</strong> Settings → Privacy → Tracking → disable for Flow XP</li>
              <li><strong>On Android:</strong> Settings → Google → Privacy → Ads</li>
            </ul>
            <p>You can also subscribe to Premium for an ad-free experience.</p>
          </section>

          <section className="legal-section">
            <h2>9. Liability and Warranties</h2>
            <p className="highlight-text">
              ⚠️ <strong>Health Warning:</strong> Flow XP is a fitness application. Consult a health professional before
              starting any training program. Flowledge cannot be held responsible for injuries resulting from the use of the
              application.
            </p>
            <p>
              The application is provided "as is", without warranty of any kind. We cannot be held responsible for direct or
              indirect damages, loss of data, service interruptions, or content posted by other users.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Suspension and Termination</h2>
            <p>We may suspend or terminate your account immediately and without notice in case of:</p>
            <ul>
              <li>Violation of these Terms & Conditions</li>
              <li>Abusive behavior or harassment</li>
              <li>Fraud or hacking attempts</li>
              <li>Repeated non-compliance with community rules</li>
            </ul>
            <p>
              You can terminate your account at any time using the "Delete My Account" function in settings. Learn more about{' '}
              <a href="/data-deletion">account deletion</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Modifications</h2>
            <p>
              We reserve the right to modify these Terms & Conditions and the application features at any time. Changes will be
              notified via the app. Your continued use constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Applicable Law and Jurisdiction</h2>
            <p>
              These Terms & Conditions are governed by French law. In case of dispute, we encourage you to contact us at{' '}
              <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a> to find an amicable solution. Failing an
              amicable resolution, any dispute will be submitted to the exclusive jurisdiction of French courts.
            </p>
          </section>

          <section className="legal-section contact-section">
            <h2>13. Contact</h2>
            <div className="contact-box">
              <p><strong>Email:</strong> <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a></p>
              <p><strong>Publisher:</strong> Flowledge</p>
              <p><strong>Application:</strong> Flow XP</p>
            </div>
          </section>

          <p className="closing-text">
            By using Flow XP, you acknowledge that you have read, understood and accepted all of these Terms and Conditions of
            Use. Happy training, Flowner! 💪
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
